import React from 'react';
import { Car, Shield, Truck, Headphones, ArrowRight } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import SectionHeader from './SectionHeader';

const Services = () => {
  const services = [
    {
      icon: Car,
      number: '01',
      title: 'Luxury Car Sales',
      description: 'Premium selection from Mercedes-Benz, BMW, Audi, Porsche, and more.',
      features: ['New & Used Vehicles', 'Certified Pre-Owned', 'Custom Orders', 'Trade-In Services'],
    },
    {
      icon: Shield,
      number: '02',
      title: 'Warranty & Protection',
      description: 'Comprehensive warranty coverage and protection plans for your investment.',
      features: ['Extended Warranty', 'Service Contracts', 'Roadside Assistance', 'Parts Protection'],
    },
    {
      icon: Truck,
      number: '03',
      title: 'Delivery & Pickup',
      description: 'Convenient delivery and pickup services for maintenance and purchases.',
      features: ['Home Delivery', 'Service Pickup', 'Nationwide Shipping', 'Express Service'],
    },
    {
      icon: Headphones,
      number: '04',
      title: '24/7 Support',
      description: 'Round-the-clock customer support and emergency assistance services.',
      features: ['Emergency Hotline', 'Online Chat', 'Mobile App', 'Customer Portal'],
    },
  ];

  return (
    <section id="services" className="relative py-28 bg-black overflow-hidden">
      <div className="absolute inset-0 section-grid-bg opacity-20 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#B0FC38]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          badge="What We Offer"
          title="Our"
          highlight="Services"
          description="Comprehensive automotive services to ensure your luxury vehicle experience is nothing short of exceptional."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <AnimateOnScroll
                key={index}
                animation={index % 2 === 0 ? 'fade-right' : 'fade-left'}
                delay={index * 100}
              >
                <div className="group premium-card glass-card p-8 sm:p-10 h-full relative overflow-hidden">
                  <span className="absolute top-6 right-8 font-display text-6xl font-bold text-white/[0.03] group-hover:text-[#3DED97]/10 transition-colors duration-500">
                    {service.number}
                  </span>

                  <div className="flex items-start gap-6 relative z-10">
                    <div className="flex-shrink-0 p-4 rounded-2xl bg-[#3DED97]/10 group-hover:bg-[#3DED97]/20 group-hover:scale-110 transition-all duration-500">
                      <Icon className="h-8 w-8 text-[#3DED97]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-2xl font-bold text-white mb-3 group-hover:text-[#3DED97] transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-400 mb-5 text-sm leading-relaxed">{service.description}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.features.map((feature, fi) => (
                          <li key={fi} className="flex items-center gap-2 text-gray-300 text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#3DED97] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        <AnimateOnScroll animation="scale-up" delay={200}>
          <div className="mt-20 relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#3DED97]/10 to-[#B0FC38]/5" />
            <div className="relative p-10 sm:p-14 text-center border border-[#3DED97]/15 rounded-3xl">
              <h3 className="font-display text-3xl font-bold text-white mb-4">
                Ready to Experience Luxury?
              </h3>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Contact us today to discover the perfect luxury vehicle tailored to your lifestyle.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:08066312634" className="btn-primary !text-base">
                  Call Now
                </a>
                <a href="#contact" className="btn-outline !text-base">
                  Get a Quote
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default Services;
